import React, { memo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useNotificationTimer } from '../hooks/useNotificationTimer';

/* ─── Design tokens enterprise ────────────────────────────────────────────
   Paleta inspirada en Linear / Vercel / Stripe Dashboard.
   Glassmorphism + dark mode completo + accesibilidad WCAG AA.
──────────────────────────────────────────────────────────────────────────── */
const TOKENS = {
  success: {
    accentBar:   'before:bg-emerald-500',
    glass:       'bg-white/85 dark:bg-zinc-900/85',
    border:      'border-emerald-100/70 dark:border-emerald-900/40',
    shadow:      'shadow-[0_8px_32px_-4px_rgba(16,185,129,0.18),0_2px_8px_-2px_rgba(0,0,0,0.06)]',
    iconBg:      'bg-emerald-50 dark:bg-emerald-900/40',
    iconText:    'text-emerald-600 dark:text-emerald-400',
    title:       'text-gray-900 dark:text-gray-50',
    subtitle:    'text-emerald-700/80 dark:text-emerald-400/80',
    time:        'text-gray-400 dark:text-gray-500',
    progress:    'from-emerald-400 via-emerald-500 to-emerald-400',
    actionPrimary: 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white focus-visible:ring-emerald-500',
    actionGhost:   'text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/40 focus-visible:ring-emerald-400',
    close:         'text-gray-400 hover:text-gray-700 hover:bg-gray-100/80 dark:hover:bg-zinc-800 dark:hover:text-gray-200 focus-visible:ring-emerald-400',
    badge:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
    ariaRole:      'status',
  },
  error: {
    accentBar:   'before:bg-rose-500',
    glass:       'bg-white/85 dark:bg-zinc-900/85',
    border:      'border-rose-100/70 dark:border-rose-900/40',
    shadow:      'shadow-[0_8px_32px_-4px_rgba(244,63,94,0.18),0_2px_8px_-2px_rgba(0,0,0,0.06)]',
    iconBg:      'bg-rose-50 dark:bg-rose-900/40',
    iconText:    'text-rose-600 dark:text-rose-400',
    title:       'text-gray-900 dark:text-gray-50',
    subtitle:    'text-rose-700/80 dark:text-rose-400/80',
    time:        'text-gray-400 dark:text-gray-500',
    progress:    'from-rose-400 via-rose-500 to-rose-400',
    actionPrimary: 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white focus-visible:ring-rose-500',
    actionGhost:   'text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/40 focus-visible:ring-rose-400',
    close:         'text-gray-400 hover:text-gray-700 hover:bg-gray-100/80 dark:hover:bg-zinc-800 dark:hover:text-gray-200 focus-visible:ring-rose-400',
    badge:         'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400',
    ariaRole:      'alert',
  },
  warning: {
    accentBar:   'before:bg-amber-400',
    glass:       'bg-white/85 dark:bg-zinc-900/85',
    border:      'border-amber-100/70 dark:border-amber-900/40',
    shadow:      'shadow-[0_8px_32px_-4px_rgba(245,158,11,0.15),0_2px_8px_-2px_rgba(0,0,0,0.06)]',
    iconBg:      'bg-amber-50 dark:bg-amber-900/40',
    iconText:    'text-amber-600 dark:text-amber-400',
    title:       'text-gray-900 dark:text-gray-50',
    subtitle:    'text-amber-700/80 dark:text-amber-400/80',
    time:        'text-gray-400 dark:text-gray-500',
    progress:    'from-amber-300 via-amber-400 to-amber-300',
    actionPrimary: 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white focus-visible:ring-amber-400',
    actionGhost:   'text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/40 focus-visible:ring-amber-400',
    close:         'text-gray-400 hover:text-gray-700 hover:bg-gray-100/80 dark:hover:bg-zinc-800 dark:hover:text-gray-200 focus-visible:ring-amber-400',
    badge:         'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
    ariaRole:      'alert',
  },
  info: {
    accentBar:   'before:bg-violet-500',
    glass:       'bg-white/85 dark:bg-zinc-900/85',
    border:      'border-violet-100/70 dark:border-violet-900/40',
    shadow:      'shadow-[0_8px_32px_-4px_rgba(139,92,246,0.15),0_2px_8px_-2px_rgba(0,0,0,0.06)]',
    iconBg:      'bg-violet-50 dark:bg-violet-900/40',
    iconText:    'text-violet-600 dark:text-violet-400',
    title:       'text-gray-900 dark:text-gray-50',
    subtitle:    'text-violet-700/80 dark:text-violet-400/80',
    time:        'text-gray-400 dark:text-gray-500',
    progress:    'from-violet-400 via-violet-500 to-violet-400',
    actionPrimary: 'bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white focus-visible:ring-violet-500',
    actionGhost:   'text-violet-700 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-900/40 focus-visible:ring-violet-400',
    close:         'text-gray-400 hover:text-gray-700 hover:bg-gray-100/80 dark:hover:bg-zinc-800 dark:hover:text-gray-200 focus-visible:ring-violet-400',
    badge:         'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-400',
    ariaRole:      'status',
  },
};

/* ─── Subcomponentes ──────────────────────────────────────────────────────── */

function NotificationAvatar({ avatar }) {
  const isUrl = typeof avatar === 'string' && avatar.startsWith('http');
  return (
    <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ring-2 ring-white dark:ring-zinc-700 shadow-sm">
      {isUrl
        ? <img src={avatar} alt="" className="w-full h-full object-cover" />
        : (
          <div className="w-full h-full bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold leading-none">
              {String(avatar).slice(0, 2).toUpperCase()}
            </span>
          </div>
        )
      }
    </div>
  );
}

function NotificationProgress({ value, tokens }) {
  return (
    <div className="mt-2.5 space-y-1">
      <div className="flex justify-between items-center">
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${tokens.time}`}>
          Progreso
        </span>
        <span className={`text-[10px] font-bold ${tokens.subtitle}`}>{value}%</span>
      </div>
      <div className="h-1.5 w-full bg-gray-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${tokens.progress} rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  );
}

function NotificationActions({ actions, tokens }) {
  if (!actions?.length) return null;
  return (
    <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-gray-100/80 dark:border-zinc-700/50">
      {actions.map((action, idx) => (
        <button
          key={idx}
          onClick={action.onClick}
          className={`
            inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold
            transition-all duration-150 select-none
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
            ${action.variant === 'primary' ? tokens.actionPrimary : tokens.actionGhost}
          `}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

function GroupedBadge({ count, tokens }) {
  if (!count || count <= 1) return null;
  return (
    <span className={`
      inline-flex items-center justify-center
      min-w-[18px] h-[18px] px-1 rounded-full
      text-[10px] font-bold leading-none
      ${tokens.badge}
    `}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

/* ─── Componente principal ────────────────────────────────────────────────── */

/**
 * BaseNotification — toast enterprise-grade.
 *
 * Soporta:
 *  - 4 tipos semánticos: success, error, warning, info
 *  - Avatar (URL o iniciales)
 *  - Acciones (botones primario y ghost)
 *  - Barra de progreso numérica (0-100)
 *  - Contador de agrupación
 *  - Modo persistente (sin auto-dismiss)
 *  - Pausa del timer al hacer hover
 *  - Cierre por teclado (Escape)
 *  - Accesibilidad WCAG AA completa
 *  - Dark mode
 *  - Glassmorphism
 */
export const BaseNotification = memo(function BaseNotification({
  notification,
  onDismiss,
  icon,
}) {
  const {
    id,
    type       = 'info',
    message,
    subtitle,
    timestamp,
    persistent = false,
    actions,
    avatar,
    progress,
    grouped,
    config,
  } = notification;

  const duration = config?.duration ?? 7000;
  const tokens   = TOKENS[type] ?? TOKENS.info;

  const { exiting, hovered, dismiss, handleMouseEnter, handleMouseLeave, handleKeyDown } =
    useNotificationTimer({ id, duration, persistent, onDismiss });

  const formattedTime = timestamp instanceof Date
    ? timestamp.toLocaleTimeString('es-ES', {
        hour:   '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : null;

  const animClass = exiting ? 'notification-exit' : 'notification-enter';

  return (
    <div className={animClass}>
      <div
        role={tokens.ariaRole}
        aria-atomic="true"
        tabIndex={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        className={`
          group relative overflow-hidden rounded-xl
          border backdrop-blur-xl
          flex flex-col
          transition-all duration-200 ease-out
          outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400
          cursor-default select-none
          ${tokens.glass}
          ${tokens.border}
          ${tokens.shadow}
          before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0
          before:w-[3px] before:rounded-l-xl
          ${tokens.accentBar}
        `}
      >
        {/* ── Cuerpo principal ── */}
        <div className="flex items-start gap-3 px-4 pt-3.5 pb-3">

          {/* Avatar o ícono */}
          {avatar
            ? <NotificationAvatar avatar={avatar} />
            : icon && (
              <div className={`
                flex-shrink-0 mt-0.5 p-2 rounded-lg
                ${tokens.iconBg} ${tokens.iconText}
              `}>
                {icon}
              </div>
            )
          }

          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className={`text-[13px] font-semibold leading-snug break-words ${tokens.title}`}>
                {message ?? 'Notificación'}
              </p>
              <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                <GroupedBadge count={grouped} tokens={tokens} />
                {persistent && (
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tokens.badge}`}>
                    fijo
                  </span>
                )}
              </div>
            </div>

            {subtitle && (
              <p className={`text-xs mt-0.5 leading-relaxed break-words ${tokens.subtitle}`}>
                {subtitle}
              </p>
            )}

            {formattedTime && (
              <p className={`text-[10px] mt-1.5 font-medium ${tokens.time}`}>
                {formattedTime}
              </p>
            )}

            {typeof progress === 'number' && (
              <NotificationProgress value={progress} tokens={tokens} />
            )}
          </div>

          {/* Botón cerrar */}
          <button
            onClick={dismiss}
            aria-label="Cerrar notificación"
            className={`
              flex-shrink-0 mt-0.5 p-1 rounded-md
              opacity-0 group-hover:opacity-100
              transition-all duration-150
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
              focus-visible:opacity-100
              ${tokens.close}
            `}
          >
            <CloseIcon style={{ fontSize: 15 }} />
          </button>
        </div>

        {/* Acciones */}
        {actions?.length > 0 && (
          <div className="px-4 pb-3">
            <NotificationActions actions={actions} tokens={tokens} />
          </div>
        )}

        {/* Barra de progreso auto-dismiss */}
        {!persistent && !exiting && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black/5 dark:bg-white/5 overflow-hidden">
            <div
              className={`
                h-full bg-gradient-to-r ${tokens.progress} rounded-full
                [animation-name:shrinkProgress]
                [animation-timing-function:linear]
                [animation-fill-mode:forwards]
                group-hover:[animation-play-state:paused]
              `}
              style={{ animationDuration: `${duration}ms` }}
              aria-hidden="true"
            />
          </div>
        )}
      </div>
    </div>
  );
});
